<template>
  <div class="consumables-logs">
    <OmxList
        :data="consumablesLogsData"
        :loading="isLoading"
        :show-search="false"
        empty-icon="cart-variant"
    >
      <template #actions v-if="currentUserAccess('ProjectLogs', UserActions.CREATE, project_id)">
        <b-button type="is-primary" icon-left="plus-thick" @click="addNewConsumableLogClicked">
          {{ $t('consumables-logs.new-consumable-log-button') }}
        </b-button>
      </template>

      <b-table-column field="date" :label="$t('consumables-logs.consumable-logdate-column')" v-slot="props">
        {{ props.row.date }}
      </b-table-column>
      <b-table-column field="resource" :label="$t('consumables-logs.consumable-resource-column')" v-slot="props">
        {{ props.row.resource }}
      </b-table-column>
      <b-table-column field="quantity" :label="$t('consumables-logs.consumable-quantity-column')" v-slot="props">
        {{ getFormattedQuantityValue(props.row.consumableLog.quantity, props.row.unit) }}
      </b-table-column>
      <b-table-column field="total" :label="$t('consumables-logs.consumable-total-column')" v-slot="props">
        {{ getFormattedQuantityValue(props.row.total, props.row.unit) }}
      </b-table-column>
      <b-table-column field="rest" :label="$t('consumables-logs.consumable-rest-column')" v-slot="props">
        {{ getFormattedQuantityValue(props.row.rest, props.row.unit) }}
      </b-table-column>
      <b-table-column field="text" :label="$t('consumables-logs.consumable-text-column')" v-slot="props">
        {{ props.row.consumableLog.text }}
      </b-table-column>
      <b-table-column
          v-if="currentUserAccess('ProjectLogs', UserActions.DELETE, project_id)"
          :label="$t('consumables-logs.actions-column')" centered v-slot="props" width="10%">
        <b-tooltip :label="$t('common.edit')" position="is-top">
          <b-button type="is-primary" size="is-small" class="m-1" icon-left="pencil"
                    @click="editConsumableLogClicked(props.row.consumableLog)" />
        </b-tooltip>
        <b-tooltip :label="$t('common.delete')" position="is-top">
          <b-button type="is-danger" size="is-small" class="m-1" icon-left="delete"
                    @click="deleteConsumableLogClicked(props.row.consumableLog)" />
        </b-tooltip>
      </b-table-column>
    </OmxList>

    <b-modal
        v-model="isConsumableLogModalActive"
        trap-focus
        :destroy-on-hide="true"
        aria-role="dialog"
        aria-modal>
      <template #default="props">
        <ConsumableLog :project_id="project_id" :consumables_data="consumablesData" :log_data="selected_log" :selected_date="selected_date"
                       @close="props.close"
                       @consumableLogAdded="readConsumablesLogsData" @consumableLogUpdated="readConsumablesLogsData" />
      </template>
    </b-modal>
  </div>
</template>

<script>

import firebase from "firebase/app";
import ConsumableLog from "@/components/ConsumableLog";
import OmxList from "@/components/OmxList.vue";

export default {
  name: "ConsumablesLogs",
  components: {
    ConsumableLog,
    OmxList
  },
  props: {
    'project_id': {
      type: String,
    },
    'selected_date': {}
  },
  data() {
    return {
      modelDate: new Date(),
      selectedUntilDate: new Date((new Date()).setHours(23, 59, 59, 0)),
      selectedFromDate: new Date((new Date()).setHours(0, 0, 0, 0)),
      consumablesData: [],
      consumablesLogsData: [],
      isLoading: false,
      isConsumableLogModalActive: false,
      selected_log: {}
    }
  },
  watch: {
    selected_date: function () {
      this.dateSelectClicked(this.selected_date)
    }
  },
  methods: {
    readConsumablesData: function () {
      this.consumablesData = [];
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getConsumablesFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getConsumablesFunction({action: "get_consumables", project_id: this.project_id, fill_meta: true})
          .then((result) => {
            if (result.data.consumables.length > 0) {
              result.data["consumables"].forEach((consumable) => {
                this.consumablesData.push({
                  consumable: consumable,
                  resource: consumable.resource
                });
              });
            }
          })
          .catch((error) => {
            this.showError(error.message)
            console.log("Error getting consumables:", error?.code);
          });
    },
    readConsumablesLogsData: function () {
      this.isConsumableLogModalActive = false;
      this.isLoading = true;
      this.consumablesLogsData = [];
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getConsumablesLogsFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const payloadData = {
        action: "get_logs",
        project_id: this.project_id,
        consumable_id: "*",
        fill_meta: true,
        filters: [['date', '>', this.selectedFromDate.toISOString()], ['date', '<', this.selectedUntilDate.toISOString()]]
      }
      getConsumablesLogsFunction(payloadData)
          .then((result) => {
            if (result.data.consumableslogs.length > 0) {
              result.data["consumableslogs"].forEach((consumableLog) => {
                this.consumablesLogsData.push({
                  consumableLog: consumableLog,
                  resource: consumableLog.parent.resource,
                  total: consumableLog.parent.logged_quantities,
                  rest: parseFloat(consumableLog.meta.estimation.quantity) - parseFloat(consumableLog.parent.logged_quantities),
                  unit: consumableLog.parent.unit,
                  date: this.getFormattedDate(consumableLog.date),
                  document_id: consumableLog.document_id
                });
              });
            }
            this.isLoading = false;
          })
          .catch((error) => {
            this.showError(error.message)
            this.isLoading = false;
            console.log("Error getting consumable logs:", error?.code);
          });
    },
    deleteConsumableLog: function (consumableLog) {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const deleteConsumableLogFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deleteConsumableLogFunction({
        action: "delete_log",
        project_id: consumableLog.project_id,
        consumable_id: consumableLog.consumable_id,
        document_id: consumableLog.document_id,
      }).then(() => {
        this.showSuccess(this.$t('messages.consumable-log-deleted'));
        this.readConsumablesLogsData()
      }).catch((error) => {
        this.showError(this.$t('messages.save-error'));
        console.error("Error deleting log:", error?.code);
      });
      this.isLoading = false;
    },
    /*
     UI Events
     */
    addNewConsumableLogClicked: function () {
      this.readConsumablesData();
      this.selected_log = {};
      this.selected_log.document_id = "new";
      this.isConsumableLogModalActive = true;
    },
    editConsumableLogClicked: function (consumableLog) {
      this.readConsumablesData();
      this.selected_log = consumableLog;
      this.isConsumableLogModalActive = true;
    },
    dateSelectClicked: function (date) {
      const fromDate = date.setHours(0, 0, 0, 0)
      const untilDate = date.setHours(23, 59, 59, 0)
      this.selectedFromDate = new Date(fromDate)
      this.selectedUntilDate = new Date(untilDate)
      this.readConsumablesLogsData();
    },
    async deleteConsumableLogClicked(consumableLog) {
      const ok = await this.$omxConfirm({
        title: this.$t('consumables-logs.delete-log-title').toString(),
        message: this.$t('consumables-logs.delete-log-message').toString(),
        confirmText: this.$t('consumables-logs.delete-log-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        icon: 'delete'
      });
      if (!ok) return;
      this.deleteConsumableLog(consumableLog);
      this.readConsumablesData();
    }
  },
  mounted() {
    this.readConsumablesLogsData();
  }
};
</script>

<style scoped>
.consumables-logs {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
